import { Plus, Scale, Trash2 } from 'lucide-react'
import { SearchSelect } from '@/components/ui/SearchSelect'
import { formatEuros } from '@/lib/format'
import type { ShareDraft } from '@/lib/recurring-draft'
import type { Beneficiary } from '@/lib/recurring-types'
import { allocateShares, isCompleteShare, sharePercent, splitEvenly } from '@/lib/shares'
import { cn } from '@/lib/utils'

export function percentTotal(shares: ShareDraft[]): number {
  return shares.reduce((sum, share) => sum + sharePercent(share.percentage), 0)
}

/**
 * Percentages per ontvanger, met het bedrag dat daar bij het huidige regel- of
 * groepstotaal uit rolt. Toont zelf wat er nog te verdelen is; het opslaan
 * wordt geblokkeerd zolang dat niet 100 is.
 */
export function DistributionEditor({
  shares,
  onChange,
  beneficiaries,
  amountCents,
  amountLabel,
  compact = false,
  readonly = false,
}: {
  shares: ShareDraft[]
  onChange: (next: ShareDraft[]) => void
  beneficiaries: Beneficiary[]
  amountCents: number
  amountLabel: string
  compact?: boolean
  readonly?: boolean
}) {
  const total = percentTotal(shares)
  const remaining = Math.round((100 - total) * 10000) / 10000
  const complete = isCompleteShare(shares)
  const percentages = shares.map((share) => sharePercent(share.percentage))
  // allocateShares deelt het hele restant uit en gaat er dus van uit dat de
  // percentages al op 100 staan. Zolang dat niet zo is, zou een lege rij een
  // bedrag claimen; dan is een rechttoe rechtaan percentage het eerlijke beeld.
  const allocated = complete
    ? allocateShares(amountCents, percentages)
    : percentages.map((percentage) => Math.round((amountCents * percentage) / 100))
  const used = new Set(shares.map((share) => share.beneficiary).filter(Boolean))

  const update = (index: number, patch: Partial<ShareDraft>) =>
    onChange(shares.map((share, i) => (i === index ? { ...share, ...patch } : share)))
  const remove = (index: number) => onChange(shares.filter((_, i) => i !== index))
  const add = () => onChange([...shares, { key: `s${Date.now()}${shares.length}`, beneficiary: '', percentage: '' }])

  return (
    <div className={cn('space-y-2', compact && 'text-sm')}>
      {shares.map((share, index) => (
        <div
          key={share.key}
          className="grid grid-cols-[minmax(0,1fr)_5.5rem_auto] items-center gap-2 sm:grid-cols-[minmax(0,1fr)_6.5rem_7rem_auto]"
        >
          <SearchSelect
            value={share.beneficiary}
            onValueChange={(value) => update(index, { beneficiary: value })}
            options={beneficiaries
              .filter((b) => b.id === share.beneficiary || !used.has(b.id))
              .map((b) => ({ value: b.id, label: b.kind === 'external' ? `${b.label} (extern)` : b.label }))}
            placeholder="Ontvanger"
            searchPlaceholder="Zoek ontvanger…"
            emptyLabel="Geen ontvanger gevonden."
            className="col-span-3 h-9 w-full sm:col-span-1"
            disabled={readonly}
          />
          <div className="relative">
            <input
              className="field num h-9 py-0 pr-5 text-right text-sm"
              inputMode="decimal"
              value={share.percentage}
              onChange={(e) => update(index, { percentage: e.target.value })}
              placeholder="0"
              aria-label="Percentage"
              disabled={readonly}
            />
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-ink-faint">%</span>
          </div>
          <span className="money text-right text-sm text-ink-soft">{formatEuros(allocated[index] / 100)}</span>
          {readonly ? (
            <span className="h-9 w-9 shrink-0" />
          ) : (
            <button
              type="button"
              className="btn-ghost h-9 w-9 shrink-0 p-0 text-ink-faint"
              aria-label="Ontvanger verwijderen"
              onClick={() => remove(index)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {readonly ? null : (
            <button type="button" className="btn-ghost h-7 px-1.5 text-sm text-accent-700" onClick={add}>
              <Plus className="h-3.5 w-3.5" /> Ontvanger
            </button>
          )}
          {!readonly && shares.length >= 2 ? (
            <button
              type="button"
              className="btn-ghost h-7 px-1.5 text-sm text-ink-soft"
              onClick={() => {
                const even = splitEvenly(shares.length)
                onChange(shares.map((share, i) => ({ ...share, percentage: even[i] })))
              }}
            >
              <Scale className="h-3.5 w-3.5" /> Verdeel gelijk
            </button>
          ) : null}
        </div>
        {shares.length > 0 ? (
          <span className={cn('text-xs', complete ? 'text-ink-faint' : 'text-status-overdue')}>
            {complete ? `100% van ${amountLabel}` : `nog ${remaining.toString().replace('.', ',')}% te verdelen`}
          </span>
        ) : null}
      </div>
    </div>
  )
}
